import { useEffect, useState } from 'react';
import { Shield, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipSettings } from '@/data/hooks/useDealershipSettings';
import { track } from '@/lib/telemetry';
import { toast } from 'sonner';

export function DealershipRulesCard() {
  const { mode, role, dealershipId, userId } = useAuth();
  const { settings, loading, saving, save } = useDealershipSettings();
  const [requireNext, setRequireNext] = useState(true);
  const [staleHours, setStaleHours] = useState(72);
  const [hotReview, setHotReview] = useState(false);
  const [minDaily, setMinDaily] = useState(0);
  const [slaHours, setSlaHours] = useState(0);

  useEffect(() => {
    setRequireNext(settings.require_next_step);
    setStaleHours(settings.stale_deal_hours);
    setHotReview(settings.hot_requires_review);
    setMinDaily(settings.min_daily_activities);
    setSlaHours(settings.followup_sla_hours);
  }, [settings]);

  if (mode !== 'real' || !(role === 'manager' || role === 'admin')) return null;

  const handleSave = async () => {
    const { error } = await save({
      require_next_step: requireNext,
      stale_deal_hours: staleHours,
      hot_requires_review: hotReview,
      min_daily_activities: minDaily,
      followup_sla_hours: slaHours,
    });
    if (error) { toast.error('Could not save rules'); return; }
    toast.success('Dealership rules saved');
    track('dealership_rules_updated', { userId, dealershipId, mode }, { requireNext, staleHours, hotReview, minDaily, slaHours });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" /> Dealership Rules
        </CardTitle>
        <CardDescription>House standards your team plays by. Reps see these as House Rules.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ToggleRow label="Every deal needs a next step" desc="Nag at create and stage advance." checked={requireNext} onChange={setRequireNext} />
        <ToggleRow label="Hot deals require manager review" desc="Flag promotions to hot for a manager glance." checked={hotReview} onChange={setHotReview} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <NumberRow id="rule-stale" label="Stale deal threshold (hours)" value={staleHours} onChange={setStaleHours} />
          <NumberRow id="rule-min" label="Min daily activities / rep" value={minDaily} onChange={setMinDaily} hint="0 = off" />
          <NumberRow id="rule-sla" label="Follow-up SLA (hours)" value={slaHours} onChange={setSlaHours} hint="0 = off" />
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save rules'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, desc, checked, onChange }: { label: string; desc: string; checked: boolean; onChange: (b: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function NumberRow({ id, label, value, onChange, hint }: { id: string; label: string; value: number; onChange: (n: number) => void; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Input id={id} type="number" min={0} value={value} onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || '0', 10)))} />
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}